import { IDisplayFieldConfig } from "projects/models";

// Core fields visible at ALL stages
const CORE_CONTACT_FIELDS = [
  "address",
  "city",
  "state",
  "zip",
  "ownerName",
  "ownerEmail",
  "ownerPhone",
  "onsiteAuditContact",
  "onsiteEmail",
  "onsitePhone",
];

const CORE_PROJECT_FIELDS = [
  "name",
  "projectTypes",
  "utilityProgram",
  "utilityProjectId",
  "customerPaymentMethod",
];

const CORE_SALES_FIELDS = [
  "salesConfigurationId",
];

// Stage order for progressive visibility
export const STAGE_ORDER = [
  "prospecting_leads",
  "ready_for_audit",
  "active_auditing",
  "engineering_prep",
  "engineering_proposal_development",
  "presenting",
  "sold_utility_upload",
  "installation",
  "closeout",
  "billing",
  "completed",
];

export const ProjectDisplaySchema: IDisplayFieldConfig[] = [
  {
    name: "prospecting_leads",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: CORE_PROJECT_FIELDS,
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: [],
      accountInformationFields: [],
    },
  },
  {
    name: "ready_for_audit",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
      ],
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: [],
      accountInformationFields: [],
    },
  },
  {
    name: "active_auditing",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
        "auditScheduleDate",
      ],
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: [],
      accountInformationFields: [],
    },
  },
  {
    name: "engineering_prep",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
      ],
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: [],
      accountInformationFields: [],
    },
  },
  {
    name: "engineering_proposal_development",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
        "sentToHcnForQuote",
        "hcnQuotePackageReceived",
        "hvacQuoteSubsCertsApproved",
      ],
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: ["hvacSubcontractorId"],
      accountInformationFields: [],
    },
  },
  {
    name: "presenting",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
        "sentToHcnForQuote",
        "hcnQuotePackageReceived",
        "hvacQuoteSubsCertsApproved",
        "proposalSentToSalesRep",
        "estimatedSaleDate",
      ],
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: ["hvacSubcontractorId"],
      accountInformationFields: [
        "soldTotalProjectValue",
        "soldTotalIncentive",
        "soldTotalCustomerPortion",
        "soldLtgValue",
        "soldHvacValue",
        "soldRefrigerationValue",
        "soldBudgetCosts",
        "soldProfit",
        "soldMargin",
      ],
    },
  },
  {
    name: "sold_utility_upload",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
        "sentToHcnForQuote",
        "hcnQuotePackageReceived",
        "hvacQuoteSubsCertsApproved",
        "proposalSentToSalesRep",
        "estimatedSaleDate",
        "soldQuarter",
        "soldMonth",
        "soldYear",
        "reviewedSignedContractDocuments",
        "submittedToUtilityForApproval",
      ],
      salesInformationFields: [
        ...CORE_SALES_FIELDS,
        "accountManagerCommissionAmount",
        "leadGeneratorCommissionAmount",
        "salesSupportCommissionAmount",
      ],
      installationInformationFields: ["hvacSubcontractorId"],
      accountInformationFields: [
        "soldTotalProjectValue",
        "soldTotalIncentive",
        "soldTotalCustomerPortion",
        "soldLtgValue",
        "soldHvacValue",
        "soldRefrigerationValue",
        "financeLender",
        "financeExpirationDate",
        "utilityExpirationDate",
        "preFunding",
        "deferment",
        "soldBudgetCosts",
        "soldProfit",
        "soldMargin",
      ],
    },
  },
  {
    name: "installation",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
        "sentToHcnForQuote",
        "hcnQuotePackageReceived",
        "hvacQuoteSubsCertsApproved",
        "proposalSentToSalesRep",
        "estimatedSaleDate",
        "soldQuarter",
        "soldMonth",
        "soldYear",
        "reviewedSignedContractDocuments",
        "submittedToUtilityForApproval",
        "submittedForFinancingApproval",
        "financingApproved",
        "utilityPreInspectionDate",
        "utilityPreInspectionDone",
        "utilityApprovalLetter",
      ],
      salesInformationFields: [
        ...CORE_SALES_FIELDS,
        "accountManagerCommissionAmount",
        "leadGeneratorCommissionAmount",
        "salesSupportCommissionAmount",
      ],
      installationInformationFields: [
        "lightingSubcontractorId",
        "hvacSubcontractorId",
        "refrigerationSubcontractorId",
        "hvacETA",
        "lightingETA",
        "refrigerationETA",
        "utilityEcd",
      ],
      accountInformationFields: [
        "soldTotalProjectValue",
        "soldTotalIncentive",
        "soldTotalCustomerPortion",
        "soldLtgValue",
        "soldHvacValue",
        "soldRefrigerationValue",
        "financeLender",
        "financeExpirationDate",
        "utilityExpirationDate",
        "preFunding",
        "deferment",
        "fiftyPercentPrefundInvoiceDate",
        "twentyFivePercentPrefundInvoiceDate",
        "soldBudgetCosts",
        "soldProfit",
        "soldMargin",
        "billedDepositDate",
        "billedDepositAmount",
        "depositReceivedDate",
        "depositReceivedAmount",
        "depositClearedDate",
      ],
    },
  },
  {
    name: "closeout",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditorId",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
        "sentToHcnForQuote",
        "hcnQuotePackageReceived",
        "hvacQuoteSubsCertsApproved",
        "proposalSentToSalesRep",
        "estimatedSaleDate",
        "soldQuarter",
        "soldMonth",
        "soldYear",
        "reviewedSignedContractDocuments",
        "submittedToUtilityForApproval",
        "submittedForFinancingApproval",
        "financingApproved",
        "utilityPreInspectionDate",
        "utilityPreInspectionDone",
        "utilityApprovalLetter",
      ],
      salesInformationFields: [
        ...CORE_SALES_FIELDS,
        "accountManagerCommissionAmount",
        "leadGeneratorCommissionAmount",
        "salesSupportCommissionAmount",
      ],
      installationInformationFields: [
        "lightingSubcontractorId",
        "hvacSubcontractorId",
        "refrigerationSubcontractorId",
        "customerWelcomeLetterSent",
        "mechanicalPOSent",
        "lightingLaborPOSent",
        "lightingMaterialPOSent",
        "refrigerationPOSent",
        "mechanicalPOAmount",
        "lightingLaborPOAmount",
        "lightingMaterialPOAmount",
        "refrigerationPOAmount",
        "hvacETA",
        "lightingETA",
        "refrigerationETA",
        "refrigerationInstalled",
        "hvacInstalled",
        "lightingInstalled",
        "totalProjectInstalledDate",
        "totalProjectCompletedDate",
        "closeoutPackageReceived",
        "closeoutPackageReviewed",
        "utilityEcd",
      ],
      accountInformationFields: [
        "soldTotalProjectValue",
        "soldTotalIncentive",
        "soldTotalCustomerPortion",
        "soldLtgValue",
        "soldHvacValue",
        "soldRefrigerationValue",
        "financeLender",
        "financeExpirationDate",
        "utilityExpirationDate",
        "preFunding",
        "deferment",
        "fiftyPercentPrefundInvoiceDate",
        "twentyFivePercentPrefundInvoiceDate",
        "soldBudgetCosts",
        "soldProfit",
        "soldMargin",
        "billedDepositDate",
        "billedDepositAmount",
        "depositReceivedDate",
        "depositReceivedAmount",
        "depositClearedDate",
      ],
    },
  },
  {
    name: "billing",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
        "sentToHcnForQuote",
        "hcnQuotePackageReceived",
        "hvacQuoteSubsCertsApproved",
        "proposalSentToSalesRep",
        "estimatedSaleDate",
        "soldQuarter",
        "soldMonth",
        "soldYear",
        "reviewedSignedContractDocuments",
        "submittedToUtilityForApproval",
        "submittedForFinancingApproval",
        "financingApproved",
        "utilityPreInspectionDate",
        "utilityPreInspectionDone",
        "utilityApprovalLetter",
      ],
      salesInformationFields: [
        ...CORE_SALES_FIELDS,
        "accountManagerCommissionAmount",
        "leadGeneratorCommissionAmount",
        "salesSupportCommissionAmount",
        "commissionPaid",
      ],
      installationInformationFields: [
        "lightingSubcontractorId",
        "hvacSubcontractorId",
        "refrigerationSubcontractorId",
        "customerWelcomeLetterSent",
        "mechanicalPOSent",
        "lightingLaborPOSent",
        "lightingMaterialPOSent",
        "refrigerationPOSent",
        "mechanicalPOAmount",
        "lightingLaborPOAmount",
        "lightingMaterialPOAmount",
        "refrigerationPOAmount",
        "hvacETA",
        "lightingETA",
        "refrigerationETA",
        "refrigerationInstalled",
        "hvacInstalled",
        "lightingInstalled",
        "totalProjectInstalledDate",
        "totalProjectCompletedDate",
        "closeoutPackageReceived",
        "closeoutPackageReviewed",
        "completionDocumentSentToCustomer",
        "closeoutPackageSubmittedToUtility",
        "utilityPostInspectionScheduleDate",
        "utilityPostInspectionCompleteDate",
        "utilityEcd",
      ],
      accountInformationFields: [
        "soldTotalProjectValue",
        "soldTotalIncentive",
        "soldTotalCustomerPortion",
        "soldLtgValue",
        "soldHvacValue",
        "soldRefrigerationValue",
        "financeLender",
        "financeExpirationDate",
        "utilityExpirationDate",
        "preFunding",
        "deferment",
        "fiftyPercentPrefundInvoiceDate",
        "twentyFivePercentPrefundInvoiceDate",
        "soldBudgetCosts",
        "soldProfit",
        "soldMargin",
        "billedDepositDate",
        "billedDepositAmount",
        "depositReceivedDate",
        "depositReceivedAmount",
        "depositClearedDate",
        "billedIncentiveDate",
        "billedIncentiveAmount",
        "billedFinancedPortionDate",
        "billedFinancedPortionAmount",
        "overageCustomerPortionDate",
        "overageCustomerPortionAmount",
        "accrualDate",
      ],
    },
  },
  {
    name: "completed",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: [
        ...CORE_PROJECT_FIELDS,
        "ownerOrTenantPayee",
        "utilityBillsReceivedAndReviewed",
        "auditRequestFormReceivedReviewed",
        "auditScheduleDate",
        "onsiteAuditDone",
        "ceToolDone",
        "hvacAssessmentFormDone",
        "hvacPrePhotosReceived",
        "sentToHcnForQuote",
        "hcnQuotePackageReceived",
        "hvacQuoteSubsCertsApproved",
        "proposalSentToSalesRep",
        "estimatedSaleDate",
        "soldQuarter",
        "soldMonth",
        "soldYear",
        "reviewedSignedContractDocuments",
        "submittedToUtilityForApproval",
        "submittedForFinancingApproval",
        "financingApproved",
        "utilityPreInspectionDate",
        "utilityPreInspectionDone",
        "utilityApprovalLetter",
      ],
      salesInformationFields: [
        ...CORE_SALES_FIELDS,
        "accountManagerCommissionAmount",
        "leadGeneratorCommissionAmount",
        "salesSupportCommissionAmount",
        "commissionPaid",
      ],
      installationInformationFields: [
        "lightingSubcontractorId",
        "hvacSubcontractorId",
        "refrigerationSubcontractorId",
        "customerWelcomeLetterSent",
        "mechanicalPOSent",
        "lightingLaborPOSent",
        "lightingMaterialPOSent",
        "refrigerationPOSent",
        "mechanicalPOAmount",
        "lightingLaborPOAmount",
        "lightingMaterialPOAmount",
        "refrigerationPOAmount",
        "hvacETA",
        "lightingETA",
        "refrigerationETA",
        "refrigerationInstalled",
        "hvacInstalled",
        "lightingInstalled",
        "totalProjectInstalledDate",
        "totalProjectCompletedDate",
        "closeoutPackageReceived",
        "closeoutPackageReviewed",
        "completionDocumentSentToCustomer",
        "closeoutPackageSubmittedToUtility",
        "utilityPostInspectionScheduleDate",
        "utilityPostInspectionCompleteDate",
        "utilityEcd",
      ],
      accountInformationFields: [
        "soldTotalProjectValue",
        "soldTotalIncentive",
        "soldTotalCustomerPortion",
        "soldLtgValue",
        "soldHvacValue",
        "soldRefrigerationValue",
        "financeLender",
        "financeExpirationDate",
        "utilityExpirationDate",
        "preFunding",
        "deferment",
        "fiftyPercentPrefundInvoiceDate",
        "twentyFivePercentPrefundInvoiceDate",
        "soldBudgetCosts",
        "soldProfit",
        "soldMargin",
        "billedDepositDate",
        "billedDepositAmount",
        "depositReceivedDate",
        "depositReceivedAmount",
        "depositClearedDate",
        "billedIncentiveDate",
        "billedIncentiveAmount",
        "billedIncentiveReceivedDate",
        "billedIncentiveReceivedAmount",
        "billedFinancedPortionDate",
        "billedFinancedPortionAmount",
        "financedPortionReceivedDate",
        "financedPortionReceivedAmount",
        "overageCustomerPortionDate",
        "overageCustomerPortionAmount",
        "overageCustomerPortionReceivedDate",
        "overageCustomerPortionReceivedAmount",
        "accrualDate",
      ],
    },
  },
  // Delayed and Lost/Dead opportunities show core fields only
  {
    name: "delayed_opportunities",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: CORE_PROJECT_FIELDS,
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: [],
      accountInformationFields: [],
    },
  },
  {
    name: "lost_dead_opportunities",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: CORE_PROJECT_FIELDS,
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: [],
      accountInformationFields: [],
    },
  },
  // Default fallback - show core fields only
  {
    name: "default",
    display: {
      contactInformationFields: CORE_CONTACT_FIELDS,
      projectInformationFields: CORE_PROJECT_FIELDS,
      salesInformationFields: CORE_SALES_FIELDS,
      installationInformationFields: [],
      accountInformationFields: [],
    },
  },
];
